<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import RealEstateCommissionPlanCompletionActions from '../components/RealEstateCommissionPlanCompletionActions.vue';
import RealEstateCommissionPlanDistributionFooter from '../components/RealEstateCommissionPlanDistributionFooter.vue';
import RealEstateCommissionPlanEditor from '../components/RealEstateCommissionPlanEditor.vue';
import RealEstateCommissionPlanProcessHeader from '../components/RealEstateCommissionPlanProcessHeader.vue';
import RealEstateCommissionPlanSaveActions from '../components/RealEstateCommissionPlanSaveActions.vue';
import RealEstateCommissionPlanSummary from '../components/RealEstateCommissionPlanSummary.vue';
import RealEstateCommissionInstallmentsEditor from '../components/RealEstateCommissionInstallmentsEditor.vue';
import { useRealEstateCommissionPlan } from '../composables/useRealEstateCommissionPlan';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const id = computed(() => route.params.salesTransactionId);
const commissionPlan = useRealEstateCommissionPlan({ id, notify: useAlert, t });
const firstName = computed(
  () => currentUser.value?.name?.split(' ')?.[0] || ''
);

const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_apoio',
    params: { accountId: route.params.accountId },
  });

const openInstallment = (installment, mode) =>
  router.push({
    name:
      mode === 'preview'
        ? 'operacao_imobiliaria_apoio_parcela_visualizar'
        : 'operacao_imobiliaria_apoio_parcela',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: route.params.salesTransactionId,
      installmentId: installment.id,
    },
  });
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <LaisHeader
      :title="t('REAL_ESTATE_COMMISSION_PLAN.TITLE')"
      :subtitle="t('REAL_ESTATE_COMMISSION_PLAN.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_COMMISSION_SUPPORT.TITLE') },
        { label: t('REAL_ESTATE_COMMISSION_PLAN.TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_COMMISSION_PLAN.ACTIONS.BACK')"
      @back="goBack"
    />
    <main
      class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <div
        v-if="commissionPlan.loading.value"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('REAL_ESTATE_COMMISSION_PLAN.COMMON.LOADING') }}
      </div>
      <p
        v-else-if="commissionPlan.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ commissionPlan.error.value }}
      </p>
      <template v-else-if="commissionPlan.plan.value">
        <RealEstateCommissionPlanProcessHeader
          :first-name="firstName"
          :process-data="commissionPlan.plan.value.dados_processo"
        />
        <RealEstateCommissionPlanSummary
          v-if="!commissionPlan.editing.value"
          :plan="commissionPlan.plan.value"
          @edit="commissionPlan.startEditing"
          @edit-installment="openInstallment($event, 'edit')"
          @preview-installment="openInstallment($event, 'preview')"
        />
        <template v-else>
          <RealEstateCommissionPlanEditor
            :form="commissionPlan.form.value"
            :errors="commissionPlan.errors.value"
            :manager-observation="
              commissionPlan.plan.value.dados_processo
                ?.ultima_observacao_gerente
            "
            :net-cents="commissionPlan.netCents.value"
            @update="commissionPlan.update"
            @change-modality="commissionPlan.setModality"
            @change-count="commissionPlan.setInstallmentCount"
          />
          <RealEstateCommissionInstallmentsEditor
            :installments="commissionPlan.form.value.installments"
            :net-cents="commissionPlan.netCents.value"
            :total-cents="commissionPlan.installmentTotalCents.value"
            :error="commissionPlan.errors.value.installments"
            @update="commissionPlan.updateInstallment"
          />
        </template>
        <RealEstateCommissionPlanDistributionFooter
          :summary="commissionPlan.plan.value.resumo_distribuicao"
        />
        <RealEstateCommissionPlanSaveActions
          v-if="commissionPlan.editing.value"
          :is-loading="commissionPlan.saving.value"
          :disabled="!commissionPlan.canSave.value"
          @save="commissionPlan.save"
        />
        <RealEstateCommissionPlanCompletionActions
          v-if="!commissionPlan.editing.value"
          :installment-count="
            commissionPlan.plan.value.parcelas_empresa?.length || 0
          "
          :sent-count="commissionPlan.sentCount.value"
          @complete="goBack"
        />
      </template>
    </main>
  </LaisGamificationSurface>
</template>
